/**
 * SixQuestionForm — signup-and-billing Phase 1 Session 5.
 *
 * Six-step wizard collecting profile fields. When `collectCredentials` is
 * true, a 7th step is appended that collects firstName/lastName/email/
 * password — the parent then runs signUp + patchProfile in sequence. When
 * false (existing-user-backfill flow), the wizard stops at step 6 and the
 * parent only patches the profile.
 *
 * Token-translated from the dashboard-arcanum `OnboardingPage.tsx` reference
 * (in `refs/`) — the C.* palette literals + inline styles became Tailwind
 * utility classes against the Intelligence Terminal token set.
 *
 * Designed as a controlled component: parent owns the submit lifecycle
 * (loading + error props). On finish, calls
 * `onComplete(values, credentials)` exactly once. `credentials` is null when
 * `collectCredentials` is false.
 */

import { useMemo, useState, type ReactElement } from 'react';
// auth-flow-hardening Inc-4 (2026-05-20): PasswordInput replaces raw
// <input type="password"> with a visibility toggle. `variant="compact"`
// matches the wizard's smaller form shape.
import { PasswordInput } from '@/components/ui/PasswordInput';
// Phase 3 § 3.3 — PostHog step-advance event (action-driven; no StrictMode guard needed).
import { posthog } from '@/lib/posthog';
import {
  validateCredentials,
  hasErrors,
  type CredentialsMode,
  type CredentialsErrors,
} from '@/lib/validate-credentials';

interface Step {
  question: string;
  field: keyof FormValues;
  placeholder: string;
  fieldLabel: string;
}

export interface FormValues {
  firm_name: string;
  // `work_role` — renamed from `role` 2026-05-25 to match the renamed DB
  // column + wire schema (eliminates field-name collision with the JWT
  // admin claim). User-facing label still says "ROLE" — only the internal
  // identifier changed.
  work_role: string;
  asset_classes: string;
  markets: string;
  focus: string;
  volume: string;
}

export interface Credentials {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

interface CredentialsState extends Credentials {
  confirmPassword: string;
  acceptedTerms: boolean;
}

const EMPTY_CREDENTIALS: CredentialsState = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: '',
  acceptedTerms: false,
};

const STEPS: readonly Step[] = [
  {
    question: "What's your firm name?",
    field: 'firm_name',
    fieldLabel: 'FIRM NAME',
    placeholder: 'e.g. Arcanum Capital',
  },
  {
    question: "What's your primary role?",
    field: 'work_role',
    fieldLabel: 'ROLE',
    placeholder: 'e.g. Operator, Developer, Broker, Lender...',
  },
  {
    question: 'What asset classes do you work in most?',
    field: 'asset_classes',
    fieldLabel: 'ASSET CLASSES',
    placeholder: 'e.g. Multifamily, Industrial, Office...',
  },
  {
    question: 'What markets are you most active in?',
    field: 'markets',
    fieldLabel: 'MARKETS',
    placeholder: 'e.g. Atlanta, Dallas, Southeast generally...',
  },
  {
    question:
      'Does your firm focus on operating asset acquisitions or new development?',
    field: 'focus',
    fieldLabel: 'FOCUS',
    placeholder: 'e.g. Mostly value-add, some ground-up...',
  },
  {
    question: 'How much have you transacted or currently manage?',
    field: 'volume',
    fieldLabel: 'VOLUME',
    placeholder: 'e.g. $200M multifamily portfolio in the Southeast...',
  },
] as const;

const MIN_LENGTH = 2;
const EMPTY_VALUES: FormValues = {
  firm_name: '',
  work_role: '',
  asset_classes: '',
  markets: '',
  focus: '',
  volume: '',
};

export interface SixQuestionFormProps {
  onComplete: (
    values: FormValues,
    credentials: Credentials | null,
    termsAcceptedAt: string
  ) => void | Promise<void>;
  loading?: boolean;
  error?: string | null;
  /**
   * Optional handler for the "Already have an account? Sign in" link
   * rendered under the Continue button. When omitted, the link is hidden.
   * Caller is responsible for any cleanup (e.g. signOut) before navigating
   * to /login.
   */
  onLoginClick?: () => void | Promise<void>;
  /**
   * Drives the wizard's 7th step:
   *   - `'full'` (or legacy `true`): self-signup — collects firstName,
   *     lastName, email, password, confirmPassword, terms. Parent runs
   *     Supabase `signUp(...)` + `patchProfile(...)`.
   *   - `'password-only'`: invited-user backfill — the `auth.users` row
   *     already carries email + first/last name (set at invite time). The
   *     magic-link session never collected a password, so step 7 collects
   *     password + confirmPassword + terms ONLY. Parent runs
   *     `supabase.auth.updateUser({password})` + `patchProfile(...)`.
   *   - `false` (or undefined): existing-user-backfill — the wizard ends at
   *     step 6 and `onComplete` receives `null` for credentials. Used when
   *     the user already has both a session AND a password (re-onboarding
   *     a self-signup user whose wizard answers were lost).
   */
  collectCredentials?: 'full' | 'password-only' | boolean;
}

export function SixQuestionForm({
  onComplete,
  loading = false,
  error = null,
  onLoginClick,
  collectCredentials = false,
}: SixQuestionFormProps): ReactElement {
  // Normalize the prop into a 3-state mode discriminator. `true` retained as
  // a legacy alias for `'full'` to avoid breaking any consumer mid-deploy.
  const credentialsMode: CredentialsMode =
    collectCredentials === 'full' || collectCredentials === true
      ? 'full'
      : collectCredentials === 'password-only'
      ? 'password-only'
      : 'none';
  const hasCredentialsStep = credentialsMode !== 'none';

  const [step, setStep] = useState(0);
  const [values, setValues] = useState<FormValues>(EMPTY_VALUES);
  const [credentials, setCredentials] =
    useState<CredentialsState>(EMPTY_CREDENTIALS);
  const [credentialsError, setCredentialsError] = useState<string | null>(null);
  // Per-field "touched" tracking — error chrome renders inline ONLY when a
  // field has been touched (focus + blur). Keeps the form quiet on first
  // render; errors surface as the user works through the step.
  const [touched, setTouched] = useState<
    Record<keyof CredentialsErrors, boolean>
  >({
    firstName: false,
    lastName: false,
    email: false,
    password: false,
    confirmPassword: false,
    acceptedTerms: false,
  });

  const TOTAL_STEPS = hasCredentialsStep ? STEPS.length + 1 : STEPS.length;
  const isCredentialsStep = hasCredentialsStep && step === STEPS.length;
  const current = isCredentialsStep ? null : STEPS[step]!;
  const currentValue = current ? values[current.field] : '';
  const profileCanContinue =
    !!current && currentValue.trim().length >= MIN_LENGTH;

  const credentialsErrors: CredentialsErrors = useMemo(
    () => validateCredentials(credentials, credentialsMode),
    [credentials, credentialsMode]
  );
  const credentialsCanContinue = !hasErrors(credentialsErrors);

  const isLastStep = step === TOTAL_STEPS - 1;
  // ToS surface appears on the FINAL step of whichever flow we're in:
  // self-signup ('full') / invited backfill ('password-only') → credentials step.
  // 'none' (backfill without credentials) → the last profile step.
  const requiresTermsAcceptance =
    isCredentialsStep || (isLastStep && credentialsMode === 'none');
  const termsGateOk = !requiresTermsAcceptance || credentials.acceptedTerms;

  const canContinue = isCredentialsStep
    ? credentialsCanContinue
    : profileCanContinue && termsGateOk;
  const progress = ((step + 1) / TOTAL_STEPS) * 100;

  const markTouched = (field: keyof CredentialsErrors) => {
    setTouched((prev) => (prev[field] ? prev : { ...prev, [field]: true }));
  };

  const handleNext = async () => {
    if (!canContinue || loading) return;
    if (isLastStep) {
      const termsAcceptedAt = new Date().toISOString();
      if (isCredentialsStep) {
        // Validate password match (extra check + clearer error than disabling)
        if (credentials.password !== credentials.confirmPassword) {
          setCredentialsError('Passwords do not match');
          return;
        }
        setCredentialsError(null);
        await onComplete(
          values,
          {
            firstName: credentials.firstName.trim(),
            lastName: credentials.lastName.trim(),
            email: credentials.email.trim(),
            password: credentials.password,
          },
          termsAcceptedAt
        );
      } else {
        await onComplete(values, null, termsAcceptedAt);
      }
    } else {
      const nextStep = step + 1;
      setStep(nextStep);
      // Phase 3 § 3.3 — fire step-advance event (action-driven, no StrictMode guard).
      // `step` is the step just COMPLETED (0-indexed); nextStep is the step the
      // user advanced TO (1-indexed for the human-readable property).
      // stepName from STEPS array or 'credentials' for the 7th step.
      const stepName =
        nextStep < STEPS.length
          ? STEPS[nextStep]?.field ?? 'unknown'
          : 'credentials';
      posthog.capture('signup_wizard_step_advanced', {
        step: nextStep,
        stepName,
      });
    }
  };

  const handleBack = () => {
    if (step > 0 && !loading) setStep(step - 1);
  };

  return (
    <div
      className="fixed inset-0 bg-void flex flex-col items-center text-text font-body"
      data-testid="six-question-form"
    >
      {/* Progress bar */}
      <div
        className="w-full h-[3px] bg-panel-border"
        data-testid="progress-bar-track"
      >
        <div
          className="h-full bg-accent transition-all duration-300 ease-out"
          style={{ width: `${progress}%` }}
          data-testid="progress-bar-fill"
        />
      </div>

      {/* Back button (top-left, visible from step 1 onward) */}
      {step > 0 && (
        <button
          type="button"
          onClick={handleBack}
          disabled={loading}
          className="absolute top-5 left-6 bg-transparent border-none text-text-muted text-xs font-mono tracking-[0.5px] cursor-pointer flex items-center gap-1 disabled:opacity-50 disabled:cursor-default"
          data-testid="back-button"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            aria-hidden="true"
          >
            <polyline points="10,3 5,8 10,13" />
          </svg>
          BACK
        </button>
      )}

      {/* Step counter (top-right) */}
      <div
        className="absolute top-5 right-6 text-[9px] font-mono text-text-dim tracking-[1px]"
        data-testid="step-counter"
      >
        {step + 1} / {TOTAL_STEPS}
      </div>

      {/* Centered content */}
      <div className="flex-1 flex flex-col items-center justify-center max-w-[440px] w-full px-6">
        {/* Logo + wordmark — matches /signup brand block */}
        <div className="mb-12 flex items-center gap-2.5">
          <svg width="20" height="24" viewBox="0 0 16 18" aria-hidden="true">
            <rect
              x="0"
              y="0"
              width="5"
              height="18"
              rx="3"
              fill="var(--color-accent)"
            />
            <rect
              x="8"
              y="0"
              width="5"
              height="18"
              rx="3"
              fill="var(--color-accent)"
            />
          </svg>
          <span className="text-xs font-bold text-text-bright tracking-[2px]">
            ARCANUM
          </span>
        </div>

        {/* Question */}
        <h2
          className="text-lg font-medium text-text-bright text-center mb-8 leading-relaxed"
          data-testid="step-question"
        >
          {isCredentialsStep
            ? credentialsMode === 'password-only'
              ? 'Set your password'
              : 'Create your account'
            : current!.question}
        </h2>

        {/* Field area — single field for profile steps; multi-field for credentials step */}
        {isCredentialsStep ? (
          <div
            className="w-full flex flex-col gap-3"
            data-testid="credentials-fields"
          >
            {credentialsMode === 'full' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label
                      htmlFor="cred-first-name"
                      className="block text-[9px] text-text-muted tracking-[1.5px] mb-1.5 uppercase font-mono"
                    >
                      FIRST NAME
                    </label>
                    <input
                      id="cred-first-name"
                      type="text"
                      autoComplete="given-name"
                      value={credentials.firstName}
                      onChange={(e) =>
                        setCredentials({
                          ...credentials,
                          firstName: e.target.value,
                        })
                      }
                      onBlur={() => markTouched('firstName')}
                      placeholder="Jane"
                      disabled={loading}
                      data-testid="cred-first-name"
                      aria-invalid={
                        touched.firstName && !!credentialsErrors.firstName
                      }
                      className="w-full px-3 py-2.5 bg-input-bg border border-input-border rounded-[3px] text-text-bright font-body text-sm outline-none transition-colors focus:border-accent/30 disabled:opacity-50 aria-[invalid=true]:border-danger/40"
                    />
                    {touched.firstName && credentialsErrors.firstName && (
                      <p
                        className="mt-1 text-[10px] font-mono text-danger"
                        data-testid="cred-first-name-error"
                      >
                        {credentialsErrors.firstName}
                      </p>
                    )}
                  </div>
                  <div>
                    <label
                      htmlFor="cred-last-name"
                      className="block text-[9px] text-text-muted tracking-[1.5px] mb-1.5 uppercase font-mono"
                    >
                      LAST NAME
                    </label>
                    <input
                      id="cred-last-name"
                      type="text"
                      autoComplete="family-name"
                      value={credentials.lastName}
                      onChange={(e) =>
                        setCredentials({
                          ...credentials,
                          lastName: e.target.value,
                        })
                      }
                      onBlur={() => markTouched('lastName')}
                      placeholder="Doe"
                      disabled={loading}
                      data-testid="cred-last-name"
                      aria-invalid={
                        touched.lastName && !!credentialsErrors.lastName
                      }
                      className="w-full px-3 py-2.5 bg-input-bg border border-input-border rounded-[3px] text-text-bright font-body text-sm outline-none transition-colors focus:border-accent/30 disabled:opacity-50 aria-[invalid=true]:border-danger/40"
                    />
                    {touched.lastName && credentialsErrors.lastName && (
                      <p
                        className="mt-1 text-[10px] font-mono text-danger"
                        data-testid="cred-last-name-error"
                      >
                        {credentialsErrors.lastName}
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="cred-email"
                    className="block text-[9px] text-text-muted tracking-[1.5px] mb-1.5 uppercase font-mono"
                  >
                    EMAIL
                  </label>
                  <input
                    id="cred-email"
                    type="email"
                    autoComplete="email"
                    value={credentials.email}
                    onChange={(e) =>
                      setCredentials({ ...credentials, email: e.target.value })
                    }
                    onBlur={() => markTouched('email')}
                    placeholder="you@company.com"
                    disabled={loading}
                    data-testid="cred-email"
                    aria-invalid={touched.email && !!credentialsErrors.email}
                    className="w-full px-3 py-2.5 bg-input-bg border border-input-border rounded-[3px] text-text-bright font-body text-sm outline-none transition-colors focus:border-accent/30 disabled:opacity-50 aria-[invalid=true]:border-danger/40"
                  />
                  {touched.email && credentialsErrors.email && (
                    <p
                      className="mt-1 text-[10px] font-mono text-danger"
                      data-testid="cred-email-error"
                    >
                      {credentialsErrors.email}
                    </p>
                  )}
                </div>
              </>
            )}
            <div>
              <label
                htmlFor="cred-password"
                className="block text-[9px] text-text-muted tracking-[1.5px] mb-1.5 uppercase font-mono"
              >
                PASSWORD
              </label>
              <PasswordInput
                id="cred-password"
                variant="compact"
                autoComplete="new-password"
                value={credentials.password}
                onChange={(e) =>
                  setCredentials({ ...credentials, password: e.target.value })
                }
                onBlur={() => markTouched('password')}
                placeholder="Min 6 characters"
                disabled={loading}
                data-testid="cred-password"
                aria-invalid={touched.password && !!credentialsErrors.password}
              />
              {touched.password && credentialsErrors.password && (
                <p
                  className="mt-1 text-[10px] font-mono text-danger"
                  data-testid="cred-password-error"
                >
                  {credentialsErrors.password}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="cred-confirm-password"
                className="block text-[9px] text-text-muted tracking-[1.5px] mb-1.5 uppercase font-mono"
              >
                CONFIRM PASSWORD
              </label>
              <PasswordInput
                id="cred-confirm-password"
                variant="compact"
                autoComplete="new-password"
                value={credentials.confirmPassword}
                onChange={(e) =>
                  setCredentials({
                    ...credentials,
                    confirmPassword: e.target.value,
                  })
                }
                onBlur={() => markTouched('confirmPassword')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canContinue) {
                    e.preventDefault();
                    void handleNext();
                  }
                }}
                placeholder="••••••••"
                disabled={loading}
                data-testid="cred-confirm-password"
                aria-invalid={
                  touched.confirmPassword && !!credentialsErrors.confirmPassword
                }
              />
              {touched.confirmPassword && credentialsErrors.confirmPassword && (
                <p
                  className="mt-1 text-[10px] font-mono text-danger"
                  data-testid="cred-confirm-password-error"
                >
                  {credentialsErrors.confirmPassword}
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="w-full">
            <label
              htmlFor={`field-${current!.field}`}
              className="block text-[9px] text-text-muted tracking-[1.5px] mb-1.5 uppercase font-mono"
            >
              {current!.fieldLabel}
            </label>
            <input
              id={`field-${current!.field}`}
              type="text"
              value={currentValue}
              onChange={(e) =>
                setValues({ ...values, [current!.field]: e.target.value })
              }
              placeholder={current!.placeholder}
              autoFocus
              disabled={loading}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canContinue) {
                  e.preventDefault();
                  void handleNext();
                }
              }}
              className="w-full px-3 py-2.5 bg-input-bg border border-input-border rounded-[3px] text-text-bright font-body text-sm outline-none transition-colors focus:border-accent/30 disabled:opacity-50"
            />
          </div>
        )}

        {/* ToS checkbox — renders on the FINAL step of each flow:
            self-signup → credentials step; backfill → last profile step. */}
        {requiresTermsAcceptance && (
          <label
            htmlFor="cred-accept-terms"
            className="flex items-start gap-2.5 mt-4 w-full cursor-pointer select-none"
            data-testid="terms-checkbox-label"
          >
            <input
              id="cred-accept-terms"
              type="checkbox"
              checked={credentials.acceptedTerms}
              onChange={(e) => {
                setCredentials({
                  ...credentials,
                  acceptedTerms: e.target.checked,
                });
                markTouched('acceptedTerms');
              }}
              onBlur={() => markTouched('acceptedTerms')}
              disabled={loading}
              data-testid="terms-checkbox"
              className="mt-0.5 w-3.5 h-3.5 accent-accent cursor-pointer disabled:cursor-default disabled:opacity-50"
            />
            <span className="text-xs text-text-muted font-mono leading-relaxed">
              I agree to the{' '}
              <a
                href="https://arcanumhq.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent hover:underline"
                data-testid="terms-link"
              >
                Terms of Service
              </a>
              .
            </span>
          </label>
        )}
        {isCredentialsStep &&
          touched.acceptedTerms &&
          credentialsErrors.acceptedTerms && (
            <p
              className="mt-1 w-full text-[10px] font-mono text-danger"
              data-testid="cred-terms-error"
            >
              {credentialsErrors.acceptedTerms}
            </p>
          )}

        {/* Error callout — parent error OR credentials-step local validation error */}
        {(error || credentialsError) && (
          <div
            className="mt-4 px-3 py-2.5 w-full bg-danger/[0.06] border border-danger/[0.13] rounded-[3px]"
            data-testid="form-error"
            role="alert"
          >
            <p className="text-danger font-mono text-sm">
              {error || credentialsError}
            </p>
          </div>
        )}

        {/* Continue / Finish CTA */}
        <button
          type="button"
          onClick={() => void handleNext()}
          disabled={!canContinue || loading}
          data-testid="continue-button"
          className="mt-8 w-full py-2.5 bg-accent border-none rounded-[3px] text-void font-mono text-[11px] font-bold tracking-[2px] transition-all cursor-pointer disabled:bg-panel-border disabled:text-text-dim disabled:cursor-default"
        >
          {loading
            ? isCredentialsStep
              ? 'CREATING ACCOUNT...'
              : 'SAVING...'
            : isLastStep
            ? isCredentialsStep
              ? 'CREATE ACCOUNT'
              : 'FINISH'
            : 'CONTINUE'}
        </button>

        {/* Sign-in link under the CTA — for users who landed here mid-onboarding
            and want to switch accounts or return to login. Caller controls
            signOut + navigation via onLoginClick. */}
        {onLoginClick && (
          <div className="mt-6 text-center">
            <span className="text-xs text-text-dim font-mono">
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => void onLoginClick()}
                data-testid="sign-in-link"
                className="text-accent font-semibold cursor-pointer bg-transparent border-none hover:underline"
              >
                Sign in
              </button>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
